// 글 에디터: WYSIWYG / 마크다운 두 모드 지원 + 다중 파일 첨부
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Category, Post, Attachment, PostFormat } from '@shared/types';
import { api, ApiError } from '../core/ApiClient';
import { push } from '../core/PushManager';
import { markdownToHtml, looksLikeMarkdown } from '../core/markdown';
import { flattenCategoryTree, categoryOptionLabel } from '../core/categoryTree';
import { DraftStore, type DraftData } from '../core/DraftStore';
import { ConfirmDialog } from './ConfirmDialog';

// 임시저장 시각을 HH:mm 로 표기
const draftTime = (ms: number): string => {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface Props {
  categories: Category[];
  initial: Post | null;
  defaultCategoryId: number;
  onSaved: (post: Post) => void;
  onCancel: () => void;
}

const TOOLS = [
  { cmd: 'bold', label: '굵게' },
  { cmd: 'italic', label: '기울임' },
  { cmd: 'underline', label: '밑줄' },
  { cmd: 'formatBlock', label: '제목', arg: 'h3' },
  { cmd: 'insertUnorderedList', label: '목록' },
  { cmd: 'insertOrderedList', label: '번호목록' },
];

export function Editor({ categories, initial, defaultCategoryId, onSaved, onCancel }: Props) {
  const areaRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<PostFormat>(initial?.format ?? 'html');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [categoryId, setCategoryId] = useState(initial?.categoryId ?? defaultCategoryId);
  const [mdText, setMdText] = useState(initial?.format === 'md' ? initial.content : '');
  const [mdPreview, setMdPreview] = useState(false);
  const [htmlSeed, setHtmlSeed] = useState(initial?.format === 'md' ? '' : initial?.content ?? '');
  const [seedVer, setSeedVer] = useState(0);

  // 모드 전환: 마크다운→에디터 시 md를 HTML로 변환해 유지(에디터→마크다운 역변환은 없음)
  const switchMode = (target: PostFormat) => {
    if (target === mode) return;
    if (target === 'html') {
      const seed = mdText.trim() ? markdownToHtml(mdText) : areaRef.current?.innerHTML ?? htmlSeed;
      setHtmlSeed(seed);
      setSeedVer((v) => v + 1);
    }
    setMode(target);
  };
  const [existing, setExisting] = useState<Attachment[]>(initial?.attachments ?? []);
  const [removedIds, setRemovedIds] = useState<number[]>([]);
  const [pending, setPending] = useState<File[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);

  // ── 임시저장(초안) ──────────────────────────────────────────────
  const draftStore = useMemo(() => new DraftStore(initial?.id ?? null), [initial?.id]);
  const [savedAt, setSavedAt] = useState<number | null>(null); // 마지막 임시저장 시각
  const [foundDraft, setFoundDraft] = useState<DraftData | null>(null); // 복원 배너용
  const [contentTick, setContentTick] = useState(0); // 에디터(contentEditable) 입력 감지
  const autosaveTimer = useRef<number | undefined>(undefined);
  const firstRun = useRef(true);

  // 현재 편집 중인 상태를 초안 형태로 수집(본문은 모드에 맞게 읽음)
  const collectDraft = (): DraftData => ({
    title,
    categoryId,
    mode,
    content: mode === 'md' ? mdText : areaRef.current?.innerHTML ?? htmlSeed,
    savedAt: Date.now(),
  });

  const draftIsEmpty = (d: DraftData): boolean =>
    !d.title.trim() && !d.content.replace(/<[^>]*>/g, '').trim();

  // 진입 시 저장된 초안이 있으면 복원 배너 노출
  useEffect(() => {
    const d = draftStore.load();
    if (d && !draftIsEmpty(d)) setFoundDraft(d);
  }, [draftStore]);

  // 자동 저장: 입력이 멈추고 1.2초 뒤 저장(빈 초안은 저장하지 않음)
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => {
      const d = collectDraft();
      if (draftIsEmpty(d)) return;
      draftStore.save(d);
      setSavedAt(d.savedAt);
    }, 1200);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [title, categoryId, mode, mdText, contentTick, draftStore]);

  const saveDraftNow = () => {
    const d = collectDraft();
    draftStore.save(d);
    setSavedAt(d.savedAt);
    setFoundDraft(null);
  };

  const restoreDraft = () => {
    if (!foundDraft) return;
    setTitle(foundDraft.title);
    setCategoryId(foundDraft.categoryId);
    setMode(foundDraft.mode);
    if (foundDraft.mode === 'md') {
      setMdText(foundDraft.content);
    } else {
      setHtmlSeed(foundDraft.content);
      setSeedVer((v) => v + 1);
      setMdText('');
    }
    setSavedAt(foundDraft.savedAt);
    setFoundDraft(null);
  };

  const discardDraft = () => {
    draftStore.clear();
    setFoundDraft(null);
    setSavedAt(null);
  };

  const exec = (cmd: string, arg?: string) => {
    document.execCommand(cmd, false, arg);
    areaRef.current?.focus();
  };

  // WYSIWYG 붙여넣기: 마크다운 텍스트면 HTML 로 변환
  const onPaste = (e: ClipboardEvent) => {
    const text = e.clipboardData?.getData('text/plain') ?? '';
    const html = e.clipboardData?.getData('text/html') ?? '';
    if (!text || html.trim() || !looksLikeMarkdown(text)) return;
    e.preventDefault();
    document.execCommand('insertHTML', false, markdownToHtml(text));
  };

  const addFiles = (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (input.files) setPending((prev) => [...prev, ...Array.from(input.files!)]);
    input.value = '';
  };

  const removeExisting = (id: number) => {
    setExisting((prev) => prev.filter((a) => a.id !== id));
    setRemovedIds((prev) => [...prev, id]);
  };

  const requestSave = () => {
    setErr('');
    if (!title.trim()) return setErr('제목을 입력하세요.');
    if (initial) setConfirmSave(true); // 수정 시 한 번 더 확인
    else void doSave();
  };

  const doSave = async () => {
    setConfirmSave(false);
    const cleanTitle = title.trim();
    const format: PostFormat = mode === 'md' ? 'md' : 'html';
    const content = mode === 'md' ? mdText : areaRef.current?.innerHTML ?? '';
    setBusy(true);
    try {
      // 이 기기의 알림 아이콘이 활성 상태일 때만 서버가 구독 기기로 브로드캐스트
      const notify = push.active;
      let post: Post;
      if (initial) {
        post = await api.updatePost(
          initial.id,
          { categoryId, title: cleanTitle, content, format },
          notify,
        );
        for (const id of removedIds) await api.deleteAttachment(id);
      } else {
        post = await api.createPost({ categoryId, title: cleanTitle, content, format }, notify);
      }
      if (pending.length > 0) await api.uploadAttachments(post.id, pending);
      draftStore.clear(); // 실제 저장 성공 → 임시저장본 정리
      onSaved(await api.getPost(post.id));
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : '저장에 실패했습니다.');
      setBusy(false);
    }
  };

  return (
    <div>
      {foundDraft && (
        <div class="draft-banner">
          <span>
            임시저장된 내용이 있습니다{' '}
            <b>({draftTime(foundDraft.savedAt)} 저장)</b>. 첨부파일은 복원되지 않습니다.
          </span>
          <div class="spacer" />
          <button class="btn btn--primary" onClick={restoreDraft}>
            불러오기
          </button>
          <button class="btn" onClick={discardDraft}>
            삭제
          </button>
        </div>
      )}

      <div class="field">
        <label>카테고리</label>
        <select
          class="select"
          value={categoryId}
          onChange={(e) => setCategoryId(Number((e.target as HTMLSelectElement).value))}
        >
          {flattenCategoryTree(categories).map((c) => (
            <option key={c.id} value={c.id}>
              {categoryOptionLabel(c)}
            </option>
          ))}
        </select>
      </div>

      <div class="field">
        <label>제목</label>
        <input
          class="input"
          value={title}
          placeholder="제목"
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
        />
      </div>

      <div class="field">
        <div class="row" style="margin-bottom:8px">
          <label style="margin:0">본문</label>
          <div class="spacer" />
          <div class="mode-switch">
            <button
              class={`mode-switch__btn ${mode !== 'md' ? 'active' : ''}`}
              onClick={() => switchMode('html')}
            >
              에디터
            </button>
            <button
              class={`mode-switch__btn ${mode === 'md' ? 'active' : ''}`}
              onClick={() => switchMode('md')}
            >
              마크다운
            </button>
          </div>
        </div>

        {mode === 'md' ? (
          <div>
            <div class="editor__toolbar" style="justify-content:flex-end">
              <button class="btn" type="button" onClick={() => setMdPreview((v) => !v)}>
                {mdPreview ? '편집' : '미리보기'}
              </button>
            </div>
            {mdPreview ? (
              <div
                class="editor__area prose"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(mdText) }}
              />
            ) : (
              <textarea
                class="editor__area md-input"
                placeholder="# 제목&#10;- 목록&#10;**굵게** `코드` 등 마크다운으로 작성"
                value={mdText}
                onInput={(e) => setMdText((e.target as HTMLTextAreaElement).value)}
              />
            )}
          </div>
        ) : (
          <div>
            <div class="editor__toolbar">
              {TOOLS.map((t) => (
                <button key={t.label} class="btn" type="button" onClick={() => exec(t.cmd, t.arg)}>
                  {t.label}
                </button>
              ))}
            </div>
            <div
              key={seedVer}
              class="editor__area prose"
              ref={areaRef}
              contentEditable
              onPaste={onPaste}
              onInput={() => setContentTick((t) => t + 1)}
              dangerouslySetInnerHTML={{ __html: htmlSeed }}
            />
          </div>
        )}
      </div>

      <div class="field">
        <label>첨부파일 (다중 선택 가능)</label>
        <input type="file" multiple onChange={addFiles} />
        <div class="attach-list">
          {existing.map((a) => (
            <span key={a.id} class="attach-chip">
              📎 {a.originalName}
              <button class="btn btn--icon btn--danger" onClick={() => removeExisting(a.id)}>
                ✕
              </button>
            </span>
          ))}
          {pending.map((f, i) => (
            <span key={`p${i}`} class="attach-chip">
              🆕 {f.name}
              <button
                class="btn btn--icon btn--danger"
                onClick={() => setPending((prev) => prev.filter((_, idx) => idx !== i))}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      </div>

      {err && <div class="msg-error">{err}</div>}

      <div class="row row--end" style="margin-top:16px">
        {savedAt && <span class="draft-status">임시저장됨 {draftTime(savedAt)}</span>}
        <button class="btn" onClick={onCancel} disabled={busy}>
          취소
        </button>
        <button class="btn" onClick={saveDraftNow} disabled={busy}>
          임시저장
        </button>
        <button class="btn btn--primary" onClick={requestSave} disabled={busy}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>

      {confirmSave && (
        <ConfirmDialog
          title="글 수정"
          message="변경 내용을 저장하시겠습니까?"
          confirmLabel="저장"
          onConfirm={doSave}
          onCancel={() => setConfirmSave(false)}
        />
      )}
    </div>
  );
}
